import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import { X, FileSearch } from "lucide-react";
import FilePreview from "@/modern/components/file-preview";
import { AnalyzeImageLayers } from "../../AnalyzeFile/components/AnalyzeImageLayers";
import { useObjectContainImageFrame } from "../../AnalyzeFile/hooks/useObjectContainImageFrame";
import { useFileDetailModal } from "../context/FileDetailModalContext";
import { isImageFileTyped } from "@/react/components/entityViews/files/components/FileHelpers";
import { cn, getFileExtension } from "@/modern/utils";
import { ImageZoomControls } from "./ImageZoomControls";
import {
  IMAGE_ZOOM_STEP,
  useImagePanZoom,
} from "../hooks/useImagePanZoom";

/**
 * Fixed stage — same height/card chrome as FileDetailRail so both columns read as peers.
 * Always fills the dialog shell so expand/contract morphs with the frame.
 */
const STAGE_CLS = cn(
  "relative mx-auto flex h-full min-h-0 w-full max-w-full flex-1 items-center justify-center",
  "overflow-hidden rounded-[2rem] bg-card",
  "border border-border/50 shadow-sm",
  "outline-none",
);
const PREVIEW_RADIUS = "rounded-[2rem]";
/** Fit the full image in the stage. Pixel size tracks the rail; max-* is the fallback before measure. */
const IMAGE_CONTAIN_CLS =
  "relative z-10 max-h-full max-w-full shrink-0 object-contain object-center";

function containFitSize(
  naturalWidth: number,
  naturalHeight: number,
  stageW: number,
  stageH: number,
): { width: number; height: number } | undefined {
  if (naturalWidth <= 0 || naturalHeight <= 0 || stageW <= 0 || stageH <= 0) return undefined;
  const fit = Math.min(stageW / naturalWidth, stageH / naturalHeight, 1);
  return { width: naturalWidth * fit, height: naturalHeight * fit };
}

/** Stage placeholder — shimmer reads as loading, not an empty peer of the Details card. */
function ImageStageSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "pointer-events-none absolute inset-0 z-10 overflow-hidden",
        PREVIEW_RADIUS,
        className,
      )}
      aria-label="Loading image"
    >
      <div className="absolute inset-0 bg-muted/70" />
      <div
        className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-foreground/[0.08] to-transparent"
        aria-hidden
      />
    </div>
  );
}

interface ImageViewerProps {
  /** Always-visible overlays (e.g. promote insight). */
  persistentOverlay?: ReactNode;
}

export const ImageViewer = ({ persistentOverlay }: ImageViewerProps) => {
  const viewerRef = useRef<HTMLDivElement>(null);
  const {
    file,
    displayedImageUrl,
    currentImageUrl,
    handleLoadComplete,
    pdfSearchText,
    clearPdfSearch,
    isAnalyzeDialogOpen,
  } = useFileDetailModal();

  /**
   * Committed paint: src + natural size so contain-fit can be computed against the stage.
   */
  const [paint, setPaint] = useState<{
    src: string;
    naturalWidth: number;
    naturalHeight: number;
  } | null>(null);
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 });

  /** Latest navigation target — ignore stale preload onLoads from rapid clicks. */
  const wantedSrcRef = useRef<string | undefined>(undefined);
  const currentImageUrlRef = useRef(currentImageUrl);
  currentImageUrlRef.current = currentImageUrl;

  const isPDF = file ? getFileExtension(file.name) === "pdf" : false;
  const isImage = file ? isImageFileTyped(file) && !isPDF : false;

  const srcToDisplay = displayedImageUrl || (isImage ? currentImageUrl : undefined);
  const isNavigating =
    isImage && !!currentImageUrl && !!displayedImageUrl && currentImageUrl !== displayedImageUrl;

  /** Target URL we should decode next (nav target, or first displayed URL). */
  const targetSrc = isNavigating ? currentImageUrl : srcToDisplay;
  wantedSrcRef.current = targetSrc;

  const commitPaint = useCallback(
    (loadedSrc: string, img: HTMLImageElement) => {
      // Stale decode from a previous rapid-nav target — ignore.
      if (loadedSrc !== wantedSrcRef.current) return;
      if (loadedSrc !== currentImageUrlRef.current) return;

      setPaint({
        src: loadedSrc,
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight,
      });
      handleLoadComplete(loadedSrc);
    },
    [handleLoadComplete],
  );

  // Drop paint when the file changes so we never flash the previous asset.
  useEffect(() => {
    setPaint(null);
  }, [file?.id]);

  // Decode off-layout (handles cache-complete images that skip onLoad).
  useEffect(() => {
    if (!isImage || !targetSrc) return;
    if (paint?.src === targetSrc) return;

    let cancelled = false;
    const img = new Image();
    const finish = () => {
      if (cancelled) return;
      commitPaint(targetSrc, img);
    };

    img.onload = finish;
    img.onerror = finish;
    img.src = targetSrc;
    if (img.complete && img.naturalWidth > 0) {
      finish();
    }

    return () => {
      cancelled = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [isImage, targetSrc, paint?.src, commitPaint]);

  useLayoutEffect(() => {
    const stage = viewerRef.current;
    if (!stage || !isImage) return;

    let frame = 0;
    const update = () => {
      const w = stage.clientWidth;
      const h = stage.clientHeight;
      if (w <= 0 || h <= 0) return;
      setStageSize((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    };
    const ro = new ResizeObserver(() => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        update();
      });
    });
    ro.observe(stage);
    update();
    return () => {
      ro.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [isImage, file?.id]);

  const imageFrame = useObjectContainImageFrame(viewerRef, [
    file?.id,
    paint?.src,
    isAnalyzeDialogOpen,
    stageSize.w,
    stageSize.h,
  ]);

  const showAnalyzeOverlay = isImage && isAnalyzeDialogOpen;
  const hasCommittedPaint = Boolean(paint && targetSrc && paint.src === targetSrc);
  const showImageSkeleton = isImage && !hasCommittedPaint;
  const containFitStyle =
    paint
      ? containFitSize(paint.naturalWidth, paint.naturalHeight, stageSize.w, stageSize.h)
      : undefined;

  const {
    scale,
    isDragging,
    isZoomed,
    canPan,
    setScale,
    reset: resetPanZoom,
    transformStyle,
    viewportHandlers,
    syncCanPan,
  } = useImagePanZoom({
    containerRef: viewerRef,
    enabled: isImage && hasCommittedPaint && !showAnalyzeOverlay,
    resetKey: file?.id,
  });

  useLayoutEffect(() => {
    if (showAnalyzeOverlay) {
      resetPanZoom();
    }
  }, [showAnalyzeOverlay, resetPanZoom]);

  useLayoutEffect(() => {
    if (!hasCommittedPaint) return;
    syncCanPan();
  }, [hasCommittedPaint, containFitStyle?.width, containFitStyle?.height, syncCanPan]);

  if (!file) return null;

  if (!isImage) {
    return (
      <div
        ref={viewerRef}
        className={cn(STAGE_CLS, "flex-col items-stretch")}
      >
        {isPDF && pdfSearchText && (
          <div className="absolute top-3 right-3 z-20 flex max-w-[280px] items-center gap-1.5 rounded-md border border-border bg-background/95 px-2.5 py-1.5 shadow-md backdrop-blur-sm">
            <FileSearch className="h-3.5 w-3.5 shrink-0 text-primary" />
            <span className="truncate text-xs text-foreground">
              {pdfSearchText.length > 50 ? `${pdfSearchText.slice(0, 50)}...` : pdfSearchText}
            </span>
            <button
              type="button"
              onClick={clearPdfSearch}
              className="flex h-5 w-5 shrink-0 items-center justify-center rounded transition-colors hover:bg-muted"
              aria-label="Clear search"
            >
              <X className="h-3 w-3 text-muted-foreground" />
            </button>
          </div>
        )}
        <div className="relative min-h-0 w-full min-w-0 flex-1">
          <FilePreview
            file={file}
            src={srcToDisplay}
            className="relative z-10 h-full w-full min-w-0"
            fileIconClassName="size-24"
            showName={false}
            disableLoadingState={true}
            enableVideoAudioRendering={true}
            searchText={pdfSearchText}
          />
        </div>
        {persistentOverlay}
      </div>
    );
  }

  return (
    <div
      ref={viewerRef}
      className={STAGE_CLS}
      aria-busy={showImageSkeleton || undefined}
    >
      {showImageSkeleton ? <ImageStageSkeleton /> : null}

      {/* Fade in only after decode — opacity only so layout never jumps */}
      <AnimatePresence mode="wait" initial={false}>
        {hasCommittedPaint && paint ? (
          <motion.div
            key={paint.src}
            className={cn(
              "absolute inset-0 z-[11] touch-none select-none",
              PREVIEW_RADIUS,
              canPan && (isDragging ? "cursor-grabbing [&_img]:!cursor-grabbing" : "cursor-grab [&_img]:!cursor-grab"),
            )}
            style={transformStyle}
            {...viewportHandlers}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
          >
            <div
              className="flex h-full w-full items-center justify-center"
              onDragStart={(event) => event.preventDefault()}
            >
              <FilePreview
                file={file}
                src={paint.src}
                className={IMAGE_CONTAIN_CLS}
                fileIconClassName="size-24"
                showName={false}
                disableLoadingState={true}
                enableVideoAudioRendering={true}
                draggable={false}
                data-file-stage-image=""
                style={containFitStyle}
              />
            </div>

            {showAnalyzeOverlay ? <AnalyzeImageLayers imageFrame={imageFrame} /> : null}
          </motion.div>
        ) : null}
      </AnimatePresence>

      <ImageZoomControls
        scale={scale}
        step={IMAGE_ZOOM_STEP}
        onScaleChange={setScale}
        onReset={resetPanZoom}
        disabled={showAnalyzeOverlay || !hasCommittedPaint}
        visible={isZoomed || isDragging}
      />

      {persistentOverlay ? (
        <div className="pointer-events-none absolute inset-0 z-20">{persistentOverlay}</div>
      ) : null}
    </div>
  );
};
